import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Container, Typography, Box, IconButton, CircularProgress, Alert,
  Avatar, List, ListItemButton, ListItemText, ListItemAvatar,
  Collapse, Slider, Button, Chip, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, useTheme, useMediaQuery, Divider,
} from '@mui/material';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SellIcon from '@mui/icons-material/Sell';
import { apiFetch } from '../apiFetch';
import EnterPriceModal from '../modals/EnterPriceModal';

interface PriceRecord {
  id: number;
  price: string;
  is_discounted: boolean;
  price_before_discount: string | null;
  created_at: string;
}

interface ShopData {
  id: number;
  name: string;
  address_line1: string;
  icon_url: string | null;
  thumbnail_url: string | null;
}

interface StoreWithPrices {
  shop: ShopData;
  current_price: string | null;
  price_history: PriceRecord[];
  has_price: boolean;
  price_status: 'unpriced' | 'cheapest' | 'most_expensive' | 'normal';
}

interface GroceryData {
  id: number;
  name: string;
  brand: string | null;
  category: string;
  packaging_size: string | null;
  image: string | null;
  image_thumbnail: string | null;
  image_icon: string | null;
}

interface ActiveShoppingList {
  id: number;
  name: string;
}

interface GroceryPriceDetails {
  grocery: GroceryData;
  stores: StoreWithPrices[];
  date_range: {
    earliest: string | null;
    latest: string | null;
  };
  active_shopping_lists: ActiveShoppingList[];
}

const ViewGroceryPrices: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [data, setData] = useState<GroceryPriceDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Expanded store state
  const [expandedStoreId, setExpandedStoreId] = useState<number | null>(null);

  // Slider state
  const [sliderValue, setSliderValue] = useState<number>(100);
  const [isHistoricalView, setIsHistoricalView] = useState(false);

  // Price modal state
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [selectedShopForPrice, setSelectedShopForPrice] = useState<ShopData | null>(null);
  const [existingPriceForModal, setExistingPriceForModal] = useState<{
    price: string;
    is_discounted: boolean;
    price_before_discount?: string | null;
  } | null>(null);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [priceToDelete, setPriceToDelete] = useState<PriceRecord | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Remove sale state
  const [removeSaleDialogOpen, setRemoveSaleDialogOpen] = useState(false);
  const [priceToRemoveSale, setPriceToRemoveSale] = useState<{ price: PriceRecord; shopId: number; priceHistory: PriceRecord[] } | null>(null);
  const [removeSaleLoading, setRemoveSaleLoading] = useState(false);

  // Sticky slider
  const [isSliderSticky, setIsSliderSticky] = useState(false);
  const sliderBoxRef = useRef<HTMLDivElement>(null);
  const sliderOriginalTop = useRef<number | null>(null);

  const apiUrl = process.env.REACT_APP_API_URL;

  // Fetch data
  const fetchData = async () => {
    try {
      const result = await apiFetch(`${apiUrl}/api/groceries/${id}/price-details/`);
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load price details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, id]);

  // Calculate slider date range
  const dateRange = useMemo(() => {
    if (!data?.date_range.earliest || !data?.date_range.latest) {
      return null;
    }
    const earliest = new Date(data.date_range.earliest);
    const latest = new Date(data.date_range.latest);
    return { earliest, latest };
  }, [data]);

  // Scroll handler for sticky slider
  const HEADER_HEIGHT = 48; // Dense Toolbar height

  const handleScroll = useCallback(() => {
    if (!sliderBoxRef.current) return;

    // Store original position on first call
    if (sliderOriginalTop.current === null) {
      const rect = sliderBoxRef.current.getBoundingClientRect();
      sliderOriginalTop.current = rect.top + window.scrollY;
    }

    // Trigger when slider would go behind the sticky header
    const scrolledPast = window.scrollY >= sliderOriginalTop.current - HEADER_HEIGHT;
    setIsSliderSticky(scrolledPast);
  }, []);

  // Set up scroll listener
  useEffect(() => {
    if (!dateRange) return;

    window.addEventListener('scroll', handleScroll, { passive: true });
    // Initial check
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [dateRange, handleScroll]);

  // Calculate the selected date based on slider value
  const selectedDate = useMemo(() => {
    if (!dateRange) return new Date();
    const { earliest, latest } = dateRange;
    const range = latest.getTime() - earliest.getTime();
    const selectedTime = earliest.getTime() + (range * sliderValue / 100);
    return new Date(selectedTime);
  }, [dateRange, sliderValue]);

  // Pre-sort price histories for efficient lookup (avoid sorting on each render)
  const sortedPriceHistories = useMemo(() => {
    if (!data) return new Map<number, PriceRecord[]>();

    const map = new Map<number, PriceRecord[]>();
    data.stores.forEach(store => {
      const sorted = [...store.price_history].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      map.set(store.shop.id, sorted);
    });
    return map;
  }, [data]);

  // Get price at a specific date for a store (expects pre-sorted price history)
  const getPriceAtDate = (shopId: number, targetDate: Date): PriceRecord | null => {
    const sortedPrices = sortedPriceHistories.get(shopId);
    if (!sortedPrices || sortedPrices.length === 0) return null;

    for (const price of sortedPrices) {
      if (new Date(price.created_at) <= targetDate) {
        return price;
      }
    }

    return null;
  };

  // Handle slider change
  const handleSliderChange = (_: Event, newValue: number | number[]) => {
    const value = newValue as number;
    setSliderValue(value);
    setIsHistoricalView(value < 100);
  };

  // Handle expand/collapse
  const handleToggleExpand = (shopId: number) => {
    setExpandedStoreId(expandedStoreId === shopId ? null : shopId);
  };

  // Handle set price click
  const handleSetPriceClick = (e: React.MouseEvent, store: StoreWithPrices) => {
    e.stopPropagation();
    setSelectedShopForPrice(store.shop);
    // Get the current price record (first in history) to pass full price info
    const currentPriceRecord = store.price_history[0];
    if (currentPriceRecord) {
      setExistingPriceForModal({
        price: currentPriceRecord.price,
        is_discounted: currentPriceRecord.is_discounted,
        price_before_discount: currentPriceRecord.price_before_discount,
      });
    } else {
      setExistingPriceForModal(null);
    }
    setPriceModalOpen(true);
  };

  // Handle price saved
  const handlePriceSaved = () => {
    setSuccessMessage('Price saved successfully.');
    setTimeout(() => setSuccessMessage(null), 4000);
    // Refresh data
    setLoading(true);
    fetchData();
  };

  // Handle delete price click
  const handleDeletePriceClick = (e: React.MouseEvent, price: PriceRecord) => {
    e.stopPropagation();
    setPriceToDelete(price);
    setDeleteDialogOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!priceToDelete) return;

    setDeleteLoading(true);
    try {
      await apiFetch(`${apiUrl}/api/prices/${priceToDelete.id}/delete/`, {
        method: 'DELETE',
      });
      setSuccessMessage('Price deleted successfully.');
      setTimeout(() => setSuccessMessage(null), 4000);
      setDeleteDialogOpen(false);
      setPriceToDelete(null);
      // Refresh data
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete price.');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Handle remove sale click
  const handleRemoveSaleClick = (e: React.MouseEvent, price: PriceRecord, shopId: number, priceHistory: PriceRecord[]) => {
    e.stopPropagation();
    setPriceToRemoveSale({ price, shopId, priceHistory });
    setRemoveSaleDialogOpen(true);
  };

  // Get the original price to restore when removing sale
  const getOriginalPrice = (salePrice: PriceRecord, priceHistory: PriceRecord[]): string | null => {
    // First try to use price_before_discount from the sale price
    if (salePrice.price_before_discount) {
      return salePrice.price_before_discount;
    }
    // Otherwise find the latest non-sale price prior to this sale
    const salePriceDate = new Date(salePrice.created_at);
    for (const p of priceHistory) {
      const pDate = new Date(p.created_at);
      if (pDate < salePriceDate && !p.is_discounted) {
        return p.price;
      }
    }
    return null;
  };

  // Handle remove sale confirmation
  const handleRemoveSaleConfirm = async () => {
    if (!priceToRemoveSale || !data) return;

    const originalPrice = getOriginalPrice(priceToRemoveSale.price, priceToRemoveSale.priceHistory);
    if (!originalPrice) {
      setError('Could not determine original price. Please enter the price manually.');
      setRemoveSaleDialogOpen(false);
      setPriceToRemoveSale(null);
      return;
    }

    setRemoveSaleLoading(true);
    try {
      await apiFetch(`${apiUrl}/api/prices/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          grocery: data.grocery.id,
          shop: priceToRemoveSale.shopId,
          price: originalPrice,
          is_discounted: false,
        }),
      });
      setSuccessMessage('Sale removed. Price restored to regular price.');
      setTimeout(() => setSuccessMessage(null), 4000);
      setRemoveSaleDialogOpen(false);
      setPriceToRemoveSale(null);
      // Refresh data
      fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to remove sale.');
    } finally {
      setRemoveSaleLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Format date for slider display
  const formatSliderDate = (date: Date): string => {
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Calculate duration at price
  const calculateDuration = (priceHistory: PriceRecord[], priceIndex: number): string => {
    if (priceIndex === 0) {
      // Current price - calculate from created_at to now
      const createdAt = new Date(priceHistory[0].created_at);
      const now = new Date();
      return formatDuration(createdAt, now);
    } else {
      // Historical price - calculate from created_at to next price's created_at
      const createdAt = new Date(priceHistory[priceIndex].created_at);
      const nextPriceDate = new Date(priceHistory[priceIndex - 1].created_at);
      return formatDuration(createdAt, nextPriceDate);
    }
  };

  // Format duration between two dates
  const formatDuration = (start: Date, end: Date): string => {
    const diffMs = end.getTime() - start.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 1) return '< 1 day';
    if (diffDays === 1) return '1 day';
    if (diffDays < 30) return `${diffDays} days`;
    if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return months === 1 ? '1 month' : `${months} months`;
    }
    const years = Math.floor(diffDays / 365);
    const remainingMonths = Math.floor((diffDays % 365) / 30);
    if (remainingMonths === 0) {
      return years === 1 ? '1 year' : `${years} years`;
    }
    return `${years}y ${remainingMonths}m`;
  };

  // Calculate price change percentage
  const calculatePriceChange = (priceHistory: PriceRecord[]): string | null => {
    if (priceHistory.length < 2) return null;

    const currentPrice = parseFloat(priceHistory[0].price);
    const oldestPrice = parseFloat(priceHistory[priceHistory.length - 1].price);

    if (oldestPrice === 0) return null;

    const changePercent = ((currentPrice - oldestPrice) / oldestPrice) * 100;
    const sign = changePercent >= 0 ? '+' : '';

    // Calculate time span
    const oldest = new Date(priceHistory[priceHistory.length - 1].created_at);
    const newest = new Date(priceHistory[0].created_at);
    const duration = formatDuration(oldest, newest);

    return `${sign}${changePercent.toFixed(1)}% over ${duration}`;
  };

  // Get border color based on price status
  const getBorderColor = (status: string): string => {
    switch (status) {
      case 'cheapest':
        return theme.palette.success.main;
      case 'most_expensive':
        return theme.palette.warning.main;
      case 'unpriced':
        return theme.palette.error.main;
      default:
        return 'transparent';
    }
  };

  // Get status label
  const getStatusLabel = (status: string): { label: string; color: 'success' | 'warning' | 'error' | 'default' } | null => {
    switch (status) {
      case 'cheapest':
        return { label: 'Cheapest', color: 'success' };
      case 'most_expensive':
        return { label: 'Most Expensive', color: 'warning' };
      case 'unpriced':
        return { label: 'Needs Price', color: 'error' };
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 5 }}>
        <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container maxWidth="md" sx={{ mt: 5 }}>
        <Alert severity="error">{error || 'Failed to load data'}</Alert>
      </Container>
    );
  }

  const { grocery, stores, active_shopping_lists } = data;

  return (
    <Container maxWidth="md" sx={{ mt: 5 }}>
      <Box className="page-header">
        <Box className="title-row">
          <IconButton onClick={() => navigate('/prices')} aria-label="back to prices">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" className="page-title">
            Prices
          </Typography>
        </Box>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Grocery info header - clickable to view grocery */}
      <Box
        component={RouterLink}
        to={`/groceries/${grocery.id}`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 3,
          p: 2,
          bgcolor: 'background.paper',
          borderRadius: 1,
          textDecoration: 'none',
          color: 'inherit',
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
      >
        <Avatar
          src={grocery.image_thumbnail || `${process.env.REACT_APP_IMAGES_URL}/placeholder-grocery.svg`}
          variant="rounded"
          sx={{ width: 60, height: 60 }}
        />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6">{grocery.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {[grocery.brand, grocery.category, grocery.packaging_size].filter(Boolean).join(' | ')}
          </Typography>
        </Box>
        <OpenInNewIcon color="action" fontSize="small" />
      </Box>

      {/* Date slider */}
      {dateRange && (
        <>
          {/* Original slider box - fades out when sticky but keeps layout space */}
          <Box
            ref={sliderBoxRef}
            sx={{
              mb: 3,
              p: 2,
              bgcolor: 'background.paper',
              borderRadius: 1,
              opacity: isSliderSticky ? 0 : 1,
              transition: 'opacity 0.15s ease',
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Price Timeline
            </Typography>
            <Slider
              value={sliderValue}
              onChange={handleSliderChange}
              aria-label="Price date slider"
              sx={{ mt: 1 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {formatSliderDate(dateRange.earliest)}
              </Typography>
              <Typography
                variant="caption"
                color={isHistoricalView ? 'primary' : 'text.secondary'}
                fontWeight={isHistoricalView ? 'bold' : 'normal'}
              >
                {isHistoricalView ? `Viewing: ${formatSliderDate(selectedDate)}` : 'Current'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatSliderDate(dateRange.latest)}
              </Typography>
            </Box>
          </Box>

          {/* Fixed slider - rendered via portal to avoid layout interference */}
          {isSliderSticky && createPortal(
            <Box
              sx={{
                position: 'fixed',
                top: 48, // Below sticky AppBar (dense Toolbar = 48px)
                left: 0,
                right: 0,
                zIndex: 1099, // Below AppBar's 1100
                py: 1,
                px: 2,
                bgcolor: 'background.paper',
                boxShadow: 3,
              }}
            >
              <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 } }}>
                <Slider
                  value={sliderValue}
                  onChange={handleSliderChange}
                  aria-label="Price date slider"
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" color="text.secondary">
                    {formatSliderDate(dateRange.earliest)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color={isHistoricalView ? 'primary' : 'text.secondary'}
                    fontWeight={isHistoricalView ? 'bold' : 'normal'}
                  >
                    {isHistoricalView ? `Viewing: ${formatSliderDate(selectedDate)}` : 'Current'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatSliderDate(dateRange.latest)}
                  </Typography>
                </Box>
              </Container>
            </Box>,
            document.body
          )}
        </>
      )}

      {/* Price view indicator - always visible */}
      {dateRange && (
        <Alert
          severity={isHistoricalView ? 'info' : 'success'}
          sx={{ mb: 2 }}
        >
          {isHistoricalView
            ? `Showing prices as of ${formatSliderDate(selectedDate)}. Drag slider to the right for current prices and to enter new prices.`
            : 'Showing current prices. Drag slider left to view historical prices.'
          }
        </Alert>
      )}

      {/* Stores list */}
      <Typography variant="h6" gutterBottom>
        Prices by Store
      </Typography>

      {stores.length === 0 ? (
        <Alert severity="info">No stores found. Add stores first to track prices.</Alert>
      ) : (
        <List sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
          {stores.map((store) => {
            const isExpanded = expandedStoreId === store.shop.id;
            const borderColor = getBorderColor(store.price_status);
            const statusInfo = getStatusLabel(store.price_status);

            // Get price to display (current or historical)
            const displayPrice = isHistoricalView
              ? getPriceAtDate(store.shop.id, selectedDate)
              : store.price_history[0] || null;

            return (
              <Box
                key={store.shop.id}
                sx={{
                  borderLeft: `4px solid ${borderColor}`,
                  mb: 1,
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <ListItemButton onClick={() => handleToggleExpand(store.shop.id)}>
                  <ListItemAvatar>
                    <Avatar
                      src={store.shop.icon_url || `${process.env.REACT_APP_IMAGES_URL}/placeholder-store.svg`}
                      variant="rounded"
                      sx={{ width: 40, height: 40 }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={store.shop.name}
                    secondary={store.shop.address_line1}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {displayPrice ? (
                      <Box sx={{ textAlign: 'right' }}>
                        {displayPrice.is_discounted && (
                          <Chip
                            label="Sale"
                            size="small"
                            color="error"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                        <Typography variant="body1" fontWeight="bold">
                          ${parseFloat(displayPrice.price).toFixed(2)}
                        </Typography>
                        {isHistoricalView && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            @ {formatDate(displayPrice.created_at)}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Chip label="No price" size="small" color="error" variant="outlined" />
                    )}
                    {!isHistoricalView && (
                      isMobile ? (
                        <IconButton
                          size="small"
                          onClick={(e) => handleSetPriceClick(e, store)}
                          color={store.has_price ? 'default' : 'error'}
                        >
                          <AttachMoneyIcon />
                        </IconButton>
                      ) : (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AttachMoneyIcon />}
                          onClick={(e) => handleSetPriceClick(e, store)}
                          sx={!store.has_price ? { color: 'error.main', borderColor: 'error.main' } : {}}
                        >
                          {store.has_price ? 'Update' : 'Set Price'}
                        </Button>
                      )
                    )}
                    <IconButton size="small">
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                </ListItemButton>

                {/* Expanded details */}
                <Collapse in={isExpanded}>
                  <Box sx={{ px: 2, pb: 2, pt: 1, bgcolor: 'action.hover' }}>
                    {/* Status */}
                    {statusInfo && (
                      <Box sx={{ mb: 2 }}>
                        <Chip
                          label={statusInfo.label}
                          color={statusInfo.color}
                          size="small"
                        />
                      </Box>
                    )}

                    {/* Price history */}
                    {store.price_history.length > 0 ? (
                      <>
                        <Typography variant="subtitle2" gutterBottom>
                          Price History
                        </Typography>

                        {/* Price change summary */}
                        {calculatePriceChange(store.price_history) && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {calculatePriceChange(store.price_history)}
                          </Typography>
                        )}

                        <List dense disablePadding>
                          {store.price_history.map((price, index) => (
                            <Box
                              key={price.id}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                py: 0.5,
                                borderBottom: index < store.price_history.length - 1 ? '1px solid' : 'none',
                                borderColor: 'divider',
                              }}
                            >
                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                                  <Typography variant="body2" component="span" fontWeight={index === 0 ? 'bold' : 'normal'}>
                                    ${parseFloat(price.price).toFixed(2)}
                                  </Typography>
                                  {price.is_discounted && (
                                    <>
                                      <Chip label="Sale" size="small" color="secondary" sx={{ height: 18, fontSize: '0.7rem' }} />
                                      <Typography variant="body2" component="span" color="text.secondary">
                                        {price.price_before_discount
                                          ? `(was $${parseFloat(price.price_before_discount).toFixed(2)})`
                                          : '(original price unknown)'}
                                      </Typography>
                                    </>
                                  )}
                                  {index === 0 && (
                                    <Chip label="Current" size="small" color="primary" sx={{ height: 18, fontSize: '0.7rem' }} />
                                  )}
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  {formatDate(price.created_at)} • {calculateDuration(store.price_history, index)}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {index === 0 && price.is_discounted && (
                                  <IconButton
                                    size="small"
                                    color="warning"
                                    onClick={(e) => handleRemoveSaleClick(e, price, store.shop.id, store.price_history)}
                                    title="Remove sale"
                                  >
                                    <SellIcon fontSize="small" sx={{ textDecoration: 'line-through' }} />
                                  </IconButton>
                                )}
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={(e) => handleDeletePriceClick(e, price)}
                                  title="Delete price"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          ))}
                        </List>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No price history available for this store.
                      </Typography>
                    )}

                    {/* Active shopping lists */}
                    {active_shopping_lists.length > 0 && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" gutterBottom>
                          On Shopping Lists
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {active_shopping_lists.map((list) => (
                            <Chip
                              key={list.id}
                              label={list.name}
                              size="small"
                              component={RouterLink}
                              to={`/shopping-lists/${list.id}`}
                              state={{ fromPrices: { groceryId: grocery.id, groceryName: grocery.name } }}
                              clickable
                            />
                          ))}
                        </Box>
                      </>
                    )}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </List>
      )}

      {/* Enter Price Modal */}
      {selectedShopForPrice && data && (
        <EnterPriceModal
          open={priceModalOpen}
          onClose={() => {
            setPriceModalOpen(false);
            setSelectedShopForPrice(null);
          }}
          onSuccess={handlePriceSaved}
          groceryId={grocery.id}
          groceryName={grocery.name}
          shopId={selectedShopForPrice.id}
          existingPrice={existingPriceForModal}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Price Record?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this price record
            {priceToDelete && ` ($${parseFloat(priceToDelete.price).toFixed(2)} from ${formatDate(priceToDelete.created_at)})`}?
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: 'warning.main' }}>
            <strong>Warning:</strong> Deleting prices affects your price history and trend analysis.
            Only delete prices that were entered by mistake.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Sale Confirmation Dialog */}
      <Dialog open={removeSaleDialogOpen} onClose={() => setRemoveSaleDialogOpen(false)}>
        <DialogTitle>Remove Sale Price?</DialogTitle>
        <DialogContent>
          {(() => {
            const originalPrice = priceToRemoveSale
              ? getOriginalPrice(priceToRemoveSale.price, priceToRemoveSale.priceHistory)
              : null;
            if (originalPrice) {
              return (
                <>
                  <DialogContentText>
                    This will create a new price record restoring the regular price of <strong>${parseFloat(originalPrice).toFixed(2)}</strong>.
                  </DialogContentText>
                  <DialogContentText sx={{ mt: 2 }}>
                    Use this when a sale has ended and you want to restore the item to its regular price.
                  </DialogContentText>
                </>
              );
            }
            return (
              <DialogContentText color="warning.main">
                No previous regular price found for this item. Please use the <strong>Update Price</strong> button to enter the current price manually.
              </DialogContentText>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveSaleDialogOpen(false)} disabled={removeSaleLoading}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={handleRemoveSaleConfirm}
            color="warning"
            variant="contained"
            disabled={removeSaleLoading || (priceToRemoveSale && !getOriginalPrice(priceToRemoveSale.price, priceToRemoveSale.priceHistory))}
          >
            {removeSaleLoading ? <CircularProgress size={20} /> : 'Remove Sale'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ViewGroceryPrices;
